import { z } from "zod";
import { capabilitySourceSchema } from "./capability-source.js";
const nonEmpty = z.string().trim().min(1);
const sha256 = z.string().regex(/^[0-9a-f]{64}$/u);
const jsonObject = z.record(z.string(), z.unknown());
export const twinDefinitionSetBuildSchema = z.object({
    name: nonEmpty.max(120),
    version: nonEmpty.max(80),
    buildSha256: sha256,
}).strict();
const relationshipRule = z.object({
    slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u),
    version: z.number().int().positive(),
    status: z.enum(["verified", "adjudicated"]),
    source: nonEmpty,
    entity: nonEmpty,
    expand: z.string().nullable(),
    quantification: z.enum(["open_world", "closed_world"]),
    predicate: jsonObject,
    emits: jsonObject,
    provenance: jsonObject,
    contentSha256: sha256,
}).strict();
const processDefinition = z.object({
    slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u),
    version: z.number().int().positive(),
    status: z.enum(["verified", "adjudicated"]),
    definition: jsonObject,
    contentSha256: sha256,
}).strict();
const processBinding = z.object({
    processSlug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u),
    processVersion: z.number().int().positive(),
    step: nonEmpty, source: nonEmpty, entity: nonEmpty, expand: z.string().nullable(),
    matchPredicate: jsonObject, confidence: z.string(), provenance: jsonObject,
    contentSha256: sha256,
}).strict();
const referenceSet = z.object({
    referenceKey: z.string().regex(/^[a-z0-9][a-z0-9._-]{0,119}$/u),
    kind: z.enum(["fx_rates", "fiscal_calendar", "value_set"]),
    version: z.number().int().positive(),
    manifest: jsonObject,
    manifestSha256: sha256,
    contentSha256: sha256,
    provenanceSha256: sha256,
    provenance: jsonObject,
}).strict();
const generatedArtifact = z.object({
    kind: z.enum(["route_card", "field_card", "contract_projection"]),
    content: jsonObject, sourceDigest: sha256, generatorDigest: sha256,
    schemaDigest: sha256, artifactDigest: sha256,
}).strict();
export const twinDefinitionSetManifestSchema = z.object({
    schemaVersion: z.literal("mlx.definition-set.v1"),
    tenantSlug: nonEmpty.max(120),
    organisationId: nonEmpty.max(200),
    canonicalizationVersion: z.literal("mlx.canonical-json.v1"),
    logicalPlan: z.object({
        kind: z.literal("derived_relation_definition_v1"),
        slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u),
        version: z.number().int().positive(),
        relationSchema: z.enum(["mlx_curated", "connector_warehouse"]),
        relationName: z.string().regex(/^[a-z][a-z0-9_]*$/u),
        normalizedContent: jsonObject,
        contentSha256: sha256,
        lineage: jsonObject,
        lineageSha256: sha256,
    }).strict(),
    compiledSqlSha256: sha256,
    productContract: z.object({
        productSlug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u),
        productVersion: z.number().int().positive(),
        publishedSchema: z.enum(["mlx_curated", "connector_warehouse"]),
        publishedRelationName: z.string().regex(/^[a-z][a-z0-9_]*$/u),
        content: jsonObject,
        contentSha256: sha256,
    }).strict(),
    publicationEvidence: z.object({
        snapshotKey: nonEmpty, snapshotKeyVersion: z.literal(2),
        legacyTreatment: z.literal("verified_v2_only"),
        validation: jsonObject, validationSha256: sha256,
        skills: z.array(z.object({
            slug: nonEmpty, version: z.number().int().positive(), status: nonEmpty,
            content: jsonObject, contentSha256: sha256,
        }).strict()),
    }).strict(),
    relationshipRules: z.array(relationshipRule),
    processDefinitions: z.array(processDefinition),
    processBindings: z.array(processBinding),
    /** Retired with the legacy cross-source linker; kept so sealed manifests still verify. Always empty. */
    entityLinkRules: z.array(z.never()).max(0),
    referenceSets: z.array(referenceSet),
    capabilityArtifacts: z.object({
        generationClass: z.enum(["native", "legacy_wrapped"]).optional(),
        capabilityKey: z.string().regex(/^[a-z][a-z0-9_]{0,79}$/u),
        revision: z.number().int().positive(),
        sourceSha256: sha256,
        generatorSha256: sha256,
        schemaSha256: sha256,
        artifactsSha256: sha256,
        artifactDigests: z.array(sha256).min(1),
        source: capabilitySourceSchema,
        generator: twinDefinitionSetBuildSchema, artifacts: z.array(generatedArtifact).min(1),
    }).strict(),
    buildClosure: z.object({
        generator: twinDefinitionSetBuildSchema,
        planner: twinDefinitionSetBuildSchema,
    }).strict(),
}).strict();
